/*
 * TURNING A VERSE INTO THE READER'S LANGUAGE, on the device, for free.
 *
 * The Hybrid the library chose: authoritative editions where they exist (the source-language
 * `original` a passage already carries IS the Hebrew or the Arabic), and — for a language no edition
 * covers — a clearly-labelled machine translation of the public-domain English. The transport is the
 * browser's own on-device translator (Chrome's Translation API): no key, no server, no cost, and the
 * text never leaves the machine. It is a progressive enhancement — absent (another browser, or the
 * pairing unavailable) the caller says so and keeps the English, never a blank.
 *
 * `TextTranslator` is the seam: the app depends on this interface, `browserTranslator()` adapts the
 * platform to it, and a test supplies a fake — so every state is exercised without the real API.
 */
export type TranslateAvailability = 'yes' | 'no' | 'after-download';

export interface TextTranslator {
  /** Whether this pairing can be translated here — ready, impossible, or needs a one-time download. */
  availability(from: string, to: string): Promise<TranslateAvailability>;
  translate(text: string, from: string, to: string): Promise<string>;
}

/** The platform's experimental on-device translator, where it exists — shape only, read defensively. */
interface OnDeviceTranslator {
  availability(opts: { sourceLanguage: string; targetLanguage: string }): Promise<string>;
  create(opts: { sourceLanguage: string; targetLanguage: string }): Promise<{ translate(text: string): Promise<string> }>;
}

declare global {
  // eslint-disable-next-line no-var
  var Translator: OnDeviceTranslator | undefined;
}

/** Adapt the browser's on-device translator to {@link TextTranslator}, or null where it is absent. */
export function browserTranslator(): TextTranslator | null {
  const api = typeof globalThis !== 'undefined' ? globalThis.Translator : undefined;
  if (!api || typeof api.availability !== 'function' || typeof api.create !== 'function') return null;
  return {
    async availability(from, to) {
      const answer = await api.availability({ sourceLanguage: from, targetLanguage: to });
      if (answer === 'available' || answer === 'readily') return 'yes';
      if (answer === 'unavailable' || answer === 'no') return 'no';
      return 'after-download';
    },
    async translate(text, from, to) {
      const instance = await api.create({ sourceLanguage: from, targetLanguage: to });
      return instance.translate(text);
    },
  };
}
